import React from 'react';
import clsx from 'clsx';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconBorderAll from '@/assets/icons/icon-border-all.svg?react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ContentDirection } from '@/components/inspector-fields/ContentDirection/ContentDirection';
import { useEditor, useNode } from '@craftjs/core';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import { NodeGapSettingsV2 } from '@/components/inspector-fields/PaddingMarginFields/NodeGapSettingsV2';
import NodeContentAlignmentSettings from '@/components/inspector-fields/ContentAlignmentHorizontal/NodeContentAlignmentSettings';
import NodeBackgroundSettings from '@/components/inspector-fields/BackgroundImage/NodeBackgroundSettings';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { NodeShapeDividerSettings } from '@/components/inspector-fields/ShapeDivider/NodeShapeDividerSettings';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';
import { useAppSelector } from '@/store/hooks';
import { selectIsFeatureEnabled } from '@/store/featureFlags/selectors';
import { PAGE_NODE_NAME } from '@/utils/constants';

interface InspectorContentProps {
  className: string;
  text: string;
}

export const InspectorContent: React.FC<InspectorContentProps> = ({
  className,
  text = 'Content',
}) => {
  const { setFlexDirection } = useContainerHelper();
  const { query } = useEditor();
  const { displayName, nodeProps, id, parent } = useNode((node) => {
    return {
      displayName: node.data.displayName,
      nodeProps: node.data.props as ContainerProps,
      parent: node.data.parent,
    };
  });
  const { getBPProp } = useBreakpointAwareProp(displayName);
  const isShapeDividerEnabled = useAppSelector(selectIsFeatureEnabled('builder4-section-divider'));
  const isRootContainer = parent ? query.node(parent).get()?.data?.name === PAGE_NODE_NAME : false;
  const flexDirection =
    getBPProp(nodeProps, 'flexDirection') === 'column' ? 'vertical' : 'horizontal';
  const handleDirectionChange = (value: 'vertical' | 'horizontal') => {
    setFlexDirection(id, value === 'vertical' ? 'column' : 'row');
  };

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
      )}
    >
      <InspectorHeader
        className="max-w-[240px] text-neutral-900"
        icon={IconBorderAll}
        text={text}
      />
      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2
            className=""
            onClick={() => {}}
            state="default"
            data-qa-selector="inspector-content-layout-element"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <p className="pb-1 text-xs font-semibold text-neutral-900">Content Area</p>
          <ContentDirection
            onClick={handleDirectionChange}
            align={flexDirection as 'vertical' | 'horizontal'}
            text="Content Direction"
            data-qa-selector="inspector-content-direction"
          />
          <NodeContentAlignmentSettings data-qa-selector="inspector-content-alignment" />

          <NodeGapSettingsV2 data-qa-selector="inspector-content-gap" />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>

          <NodeColorField
            propertyName="backgroundColor"
            solidPropertyName="bgSolid"
            gradientPropertyName="bgGradient"
            gradientEnabled
            label="Fill"
            data-qa-selector="inspector-content-fill-color"
          />

          <NodeColorField
            propertyName="borderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Stroke Color"
            data-qa-selector="inspector-content-stroke-color"
          />
          <NodeColorField
            propertyName="hoverBorderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Hover Stroke"
            data-qa-selector="inspector-content-hover-stroke"
          />

          <NodeStrokeSettings data-qa-selector="inspector-content-stroke" />
          <NodeShadowField data-qa-selector="inspector-content-shadow" />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeBackgroundSettings data-qa-selector="inspector-content-background" />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeAnimationSettings showStagger />

          {isRootContainer && isShapeDividerEnabled && (
            <>
              <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
              <p className="pb-1 text-xs font-semibold text-neutral-900">Divider</p>
              <NodeShapeDividerSettings data-qa-selector="inspector-content-shape-divider" />
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
